import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useRef, useState } from 'react';

import { getRole } from '@client/new/components/AdminMenu/helpers';
import { toAsync } from '@client/new/helpers/reactQuery';
import type { Async } from '@client/new/helpers/types';

import { isKnownAutomationType } from '../automation-copy';
import {
  CreatorAutomationItem,
  CreatorAutomationPatchResponse,
  CreatorAutomationResponse,
  UpdateAutomationConfigRequest,
  UpdateCreatorAutomationRequest,
  getCreatorAutomations,
  updateCreatorAutomation,
} from '../automations.api';
import { serverErrorMessage } from '../automations.errors';
import { automationKeys } from '../automations.keys';

export type AutomationsModel = {
  automations: CreatorAutomationItem[];
  pending: ReadonlySet<string>;
  errors: Partial<Record<string, AutomationsUpdateError>>;
  statuses: Partial<Record<string, AutomationsUpdateStatus>>;
  /** Admin and moderator sessions see the pilot tooling (config fields,
   *  send test, enrolled people, stats) under the list. */
  adminTools: boolean;
  retry: () => Promise<void>;
  /** Resolve true when the server accepted the change; the failure itself
   *  is recorded per row in `errors`. */
  update: (
    automationType: string,
    enabled: boolean,
    config?: UpdateAutomationConfigRequest,
  ) => Promise<boolean>;
  saveConfig: (
    automationType: string,
    config: UpdateAutomationConfigRequest,
  ) => Promise<boolean>;
};

export type AutomationsRequestErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'network';

/**
 * `rejected` is creator-api's 400 with a business reason (fail-closed
 * enable gate, config validation). `message` is the server's text and the
 * only thing worth showing: retrying the same request cannot succeed.
 * The other kinds carry `intendedEnabled` when a toggle failed, so the View
 * can offer a retry with the same intent.
 */
export type AutomationsUpdateError =
  | { kind: 'rejected'; message: string; onEnable: boolean }
  | { kind: AutomationsRequestErrorKind; intendedEnabled?: boolean };

export type AutomationsUpdateStatus = { kind: 'saving' | 'saved' };

export type AutomationsLoadError = {
  kind: AutomationsRequestErrorKind | 'contract';
  retry: () => Promise<void>;
};

class AutomationsContractError extends Error {}

/** The list is server-driven (GRW-530): a type this build has no copy for is
 *  still rendered from the server's fields. The breadcrumb tells us a copy
 *  entry (icon, money label) is due. */
const toAutomation = (
  raw: CreatorAutomationResponse,
): CreatorAutomationItem => {
  if (!isKnownAutomationType(raw.automationType)) {
    Sentry.captureMessage(
      `Unsupported creator automation type: ${raw.automationType}`,
      'info',
    );
  }
  return raw;
};

const statusOf = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

export const errorKindOf = (error: unknown): AutomationsRequestErrorKind => {
  switch (statusOf(error)) {
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    default:
      return 'network';
  }
};

const toUpdateError = (
  error: unknown,
  intendedEnabled: boolean | undefined,
): AutomationsUpdateError => {
  const message = serverErrorMessage(error);
  if (statusOf(error) === 400 && message) {
    return { kind: 'rejected', message, onEnable: intendedEnabled === true };
  }
  return { kind: errorKindOf(error), intendedEnabled };
};

const loadErrorKindOf = (error: unknown): AutomationsLoadError['kind'] =>
  error instanceof AutomationsContractError ? 'contract' : errorKindOf(error);

const withEnabled = (
  automations: CreatorAutomationItem[] | undefined,
  automationType: string,
  enabled: boolean,
): CreatorAutomationItem[] =>
  (automations ?? []).map((item) =>
    item.automationType === automationType ? { ...item, enabled } : item,
  );

/** The PATCH answer carries only the toggle and config, so it is merged
 *  into the list row: copy, cadence and channels come from the last GET.
 *  An accepted enable means the gate passed, so `needs_setup` clears
 *  locally until the next refetch recomputes it. */
const withPatched = (
  automations: CreatorAutomationItem[] | undefined,
  patched: CreatorAutomationPatchResponse,
): CreatorAutomationItem[] =>
  (automations ?? []).map((item) =>
    item.automationType === patched.automationType
      ? {
          ...item,
          ...patched,
          status: patched.enabled
            ? 'enabled'
            : item.status === 'needs_setup'
            ? 'needs_setup'
            : 'disabled',
          readinessReason: patched.enabled ? null : item.readinessReason,
        }
      : item,
  );

type PatchVariables = {
  automationType: string;
  body: UpdateCreatorAutomationRequest;
};

export const useAutomationsModel = (
  creatorId: number,
): Async<AutomationsModel, AutomationsLoadError> => {
  const queryClient = useQueryClient();
  const queryKey = automationKeys.list(creatorId);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const automations = (await getCreatorAutomations(creatorId)).map(
        toAutomation,
      );
      if (automations.length === 0) {
        throw new AutomationsContractError('No automations in the response');
      }
      return automations;
    },
    retry: false,
  });

  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const pendingRef = useRef<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<
    Partial<Record<string, AutomationsUpdateError>>
  >({});
  const [statuses, setStatuses] = useState<
    Partial<Record<string, AutomationsUpdateStatus>>
  >({});

  const releasePending = (automationType: string) => {
    const next = new Set(pendingRef.current);
    next.delete(automationType);
    pendingRef.current = next;
    setPending(next);
  };

  const mutation = useMutation<
    CreatorAutomationPatchResponse,
    unknown,
    PatchVariables
  >({
    mutationFn: ({ automationType, body }) =>
      updateCreatorAutomation(creatorId, automationType, body),
    retry: false,
    onSuccess: (patched, { automationType }) => {
      queryClient.setQueryData<CreatorAutomationItem[]>(queryKey, (current) =>
        withPatched(current, patched),
      );
      setStatuses((current) => ({
        ...current,
        [automationType]: { kind: 'saved' },
      }));
      // Readiness and stats may have moved server-side; refresh both in the
      // background so the badges and the drawer catch up.
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({
        queryKey: automationKeys.stats(creatorId),
      });
    },
    /**
     * Rollback flips `enabled` back instead of restoring a snapshot, which
     * holds only because the optimistic patch in `patch` touches no other
     * field: config is never applied optimistically, the canonical row from
     * onSuccess is the first time the cache sees it.
     */
    onError: (error, { automationType, body }) => {
      if (body.enabled !== undefined) {
        const enabled = body.enabled;
        queryClient.setQueryData<CreatorAutomationItem[]>(queryKey, (current) =>
          withEnabled(current, automationType, !enabled),
        );
      }
      setErrors((current) => ({
        ...current,
        [automationType]: toUpdateError(error, body.enabled),
      }));
      setStatuses((current) => ({ ...current, [automationType]: undefined }));
    },
    onSettled: (_data, _error, { automationType }) =>
      releasePending(automationType),
  });

  const refetch = async () => {
    await query.refetch();
  };

  const data = toAsync(query, (error) => ({
    kind: loadErrorKindOf(error),
    retry: refetch,
  }));

  if (data.type !== 'fetched') return data;

  const role = getRole();
  const patch = async (
    automationType: string,
    body: UpdateCreatorAutomationRequest,
  ): Promise<boolean> => {
    if (pendingRef.current.has(automationType)) return false;
    const exists = data.value.some(
      (item) => item.automationType === automationType,
    );
    if (!exists) return false;

    pendingRef.current = new Set(pendingRef.current).add(automationType);
    setPending(pendingRef.current);
    setStatuses((current) => ({
      ...current,
      [automationType]: { kind: 'saving' },
    }));
    setErrors((current) => ({ ...current, [automationType]: undefined }));
    if (body.enabled !== undefined) {
      const enabled = body.enabled;
      queryClient.setQueryData<CreatorAutomationItem[]>(queryKey, (current) =>
        withEnabled(current, automationType, enabled),
      );
    }

    return mutation.mutateAsync({ automationType, body }).then(
      () => true,
      // Outcome is recorded in `errors` by onError; the View reads it per row.
      () => false,
    );
  };

  return {
    type: 'fetched',
    value: {
      automations: data.value,
      pending,
      errors,
      statuses,
      adminTools: role === 'admin' || role === 'moderator',
      retry: refetch,
      update: (automationType, enabled, config) =>
        patch(automationType, config ? { enabled, config } : { enabled }),
      saveConfig: (automationType, config) => patch(automationType, { config }),
    },
  };
};
